import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, InputLabel, MenuItem, Select } from '@mui/material';
import LeadGenerationContext from '../../../context/LeadGenerationContext';
import { REQUIRED } from '../../../constants';

const useStyles = {
  date_of_birth_select: {
    background: 'rgba(85, 88, 88, 0.02) 0% 0% no-repeat padding-box',
    opacity: 1,
    height: '41px',
    fontWeight: 300,
    fontStyle: 'normal',
    fontSize: '14px',
    lineHeight: '16px',
    textAlign: 'left',
    letterSpacing: '0.1px',
    color: '#000000',
    '& .MuiOutlinedInput-input': {
      paddingTop: '11.5px',
      paddingBottom: '11.5px',
    },
    '@media (max-width:650px)': {
      '& .MuiSelect-iconOutlined': {
        left: '400px !important',
      },
      '& .MuiSelect-selectMenu': {
        overflow: 'visible',
      },
    },
  },
  dob_month_year_select_vi: {
    '& .MuiSelect-outlined': {
      paddingRight: '10px',
      paddingLeft: '10px',
    },
    '& .MuiSelect-iconOutlined': {
      left: '45px !important',
    },
  },
  select_icon: {
    color: '#661C69',
    opacity: 1,
    '@media (max-width:340px)': {
      right: 0,
    },
    '@media (min-width:960px) and (max-width:1024px)': {
      right: 0,
    },
  },
  dob_month_year_select: {
    '& .MuiSelect-outlined': {
      paddingRight: '10px',
      paddingLeft: '15px',
    },
    '@media (max-width:650px)': {
      '& .MuiSelect-iconOutlined': {
        left: '60px !important',
        '@media (max-width:325px)': {
          left: '55px !important',
        },
      },
      '& .MuiSelect-selectMenu': {
        overflow: 'visible',
      },
    },
  },
  label_title: {
    marginBottom: '5px',
    font: 'normal normal 300 14px/16px Arial',
    letterSpacing: '0.1px',
    color: '#000000',
    textAlign: 'left',
  },
};

export default function ExpiryDate() {
  const { t } = useTranslation();
  const classes = useStyles;
  const leadGenerationContext = useContext(LeadGenerationContext);
  const { fundData, setFundData, setFundDataErrors, setIsFundingScreenDirty } =
    leadGenerationContext;

  const handleChange = (e) => {
    setIsFundingScreenDirty(true);
    switch (e.target.name) {
      case 'expiryMonth':
        if (e.target.value) {
          if (e.target.value === 'MMM') {
            setFundData({ ...fundData, [e.target.name]: e.target.value });
            setFundDataErrors((prevState) => ({
              ...prevState,
              expiryDate: '',
              [e.target.name]: '',
            }));
          } else {
            setFundData({ ...fundData, [e.target.name]: e.target.value });
            setFundDataErrors((prevState) => ({
              ...prevState,
              [e.target.name]: '',
            }));
          }
        } else {
          setFundData({ ...fundData, [e.target.name]: e.target.value });
        }
        break;

      case 'expiryYear':
        if (e.target.value) {
          if (e.target.value === 'YYYY') {
            setFundData({ ...fundData, [e.target.name]: e.target.value });
            setFundDataErrors((prevState) => ({
              ...prevState,
              [e.target.name]: '',
              expiryDate: '',
            }));
          } else {
            setFundData({ ...fundData, [e.target.name]: e.target.value });
            setFundDataErrors((prevState) => ({
              ...prevState,
              [e.target.name]: '',
            }));
          }
        } else {
          setFundData((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value,
          }));
        }
        break;

      default:
        break;
    }
  };

  const yearGenerator = () => {
    const years = [];
    for (let startYear = 2021; startYear <= 2039; startYear++) {
      years.push(startYear);
    }
    return years;
  };

  const monthGenerator = () => [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  return (
    <>
      <InputLabel
        htmlFor="expiryMonth"
        data-testid="expiryDateLabel_id"
        // required
        aria-label={`${t('Expiry Date', { defaultValue: 'Expiry Date' })} ${t(REQUIRED, {
          defaultValue: REQUIRED,
        })}`}
        className={classes.label_title}
      >
        {t('Expiry Date', { defaultValue: 'Expiry Date' })}
      </InputLabel>
      <Grid container>
        <Grid size={{ xs: 4 }} className="dob-select-day">
          <Select
            id="expiryMonth"
            data-testid="expiryMonth_id"
            name="expiryMonth"
            variant="outlined"
            fullWidth
            inputProps={{ 'data-testid': 'expiry_month' }}
            role="listbox"
            aria-label={`${t('Aria Label Expiry Month', {
              defaultValue: 'Select Expiry Month in Expiry Date dropdown Required',
            })}`}
            value={fundData?.expiryMonth ? fundData.expiryMonth : 'MMM'}
            onChange={handleChange}
            className={`${classes.date_of_birth_select} ${classes.dob_month_year_select}`}
            classes={{
              icon: classes.select_icon,
            }}
          >
            <MenuItem value="MMM" aria-label="MMM">
              {t('MMM', { defaultValue: 'MMM' })}
            </MenuItem>
            {monthGenerator().length !== 0
              ? monthGenerator().map((month) => (
                  <MenuItem key={month} aria-label={month} value={month}>
                    {t(month, { defaultValue: month })}
                  </MenuItem>
                ))
              : ''}
          </Select>
        </Grid>
        <Grid size={{ xs: 4 }} className="dob-select">
          <Select
            id="expiryYear"
            data-testid="expiryYear_id"
            name="expiryYear"
            inputProps={{ 'data-testid': 'expiry_year' }}
            variant="outlined"
            fullWidth
            value={fundData?.expiryYear ? fundData.expiryYear : 'YYYY'}
            role="listbox"
            aria-label={`${t('Aria Label Expiry Year', {
              defaultValue: 'Select Expiry Year in Expiry Date dropdown Required',
            })}`}
            onChange={handleChange}
            className={`${classes.date_of_birth_select} ${classes.dob_month_year_select}`}
            classes={{
              icon: classes.select_icon,
            }}
          >
            <MenuItem value="YYYY" aria-label="YYYY">
              {t('YYYY', { defaultValue: 'YYYY' })}
            </MenuItem>
            {yearGenerator().length !== 0
              ? yearGenerator().map((year) => (
                  <MenuItem key={year} aria-label={year} value={year}>
                    {year}
                  </MenuItem>
                ))
              : ''}
          </Select>
        </Grid>
        <Grid
          size={{ xs: 4 }}
          className="dob-select-empty"
          style={{ backgroundColor: 'rgba(85, 88, 88, 0.04)' }}
        />
      </Grid>
    </>
  );
}
